import { observer } from "mobx-react";
import React, { useCallback, useState } from "react";
import { useRootStore } from "../../stores";
import "./styles.css";
import { Clock, Colors, LOGO2 } from "../../assets/";
import { useTranslation } from "react-i18next";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { AppPopup, PlaceCategory, PlaceOpenHoursRow } from "../../components";
import moment from "moment-timezone";
import { OpenHour } from "../../interfaces_and_types";
import { v4 as uuidv4 } from "uuid";

const Place = () => {
  const { place, setPlaceStatus } = useRootStore().rootStore.placeStore;
  const { i18n, t } = useTranslation();
  const [isItems, setIsItems] = useState(true);
  const [openHoursPopUpIsOpen, setOpenHoursPopUpIsOpen] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);

  const isBusy = place?.status === 2;
  const isAvailable = place?.status === 1;

  const toggleBusyMode = async () => {
    if (!place || statusUpdating) return;
    // Only toggle between available (1) and busy (2)
    if (place.status !== 1 && place.status !== 2) return;
    setStatusUpdating(true);
    await setPlaceStatus(isBusy ? 1 : 2);
    setStatusUpdating(false);
  };

  const initializeOpenHours = useCallback(() => {
    const days: OpenHour[] = [
      {
        day: "Sunday",
        open: place?.openHours.find((v) => v.day === "Sunday")?.open || null,
        close: place?.openHours.find((v) => v.day === "Sunday")?.close || null,
      },
      {
        day: "Monday",
        open: place?.openHours.find((v) => v.day === "Monday")?.open || null,
        close: place?.openHours.find((v) => v.day === "Monday")?.close || null,
      },
      {
        day: "Tuesday",
        open: place?.openHours.find((v) => v.day === "Tuesday")?.open || null,
        close: place?.openHours.find((v) => v.day === "Tuesday")?.close || null,
      },
      {
        day: "Wednesday",
        open: place?.openHours.find((v) => v.day === "Wednesday")?.open || null,
        close:
          place?.openHours.find((v) => v.day === "Wednesday")?.close || null,
      },
      {
        day: "Thursday",
        open: place?.openHours.find((v) => v.day === "Thursday")?.open || null,
        close:
          place?.openHours.find((v) => v.day === "Thursday")?.close || null,
      },
      {
        day: "Friday",
        open: place?.openHours.find((v) => v.day === "Friday")?.open || null,
        close: place?.openHours.find((v) => v.day === "Friday")?.close || null,
      },
      {
        day: "Saturday",
        open: place?.openHours.find((v) => v.day === "Saturday")?.open || null,
        close:
          place?.openHours.find((v) => v.day === "Saturday")?.close || null,
      },
    ];
    return days;
  }, [place?.openHours]);

  const convertToLocalTimeZone = (
    utcDatetime: string | null | undefined,
    timezone: string,
  ) => {
    const utcCutoff = moment.utc(utcDatetime, "HH:mm:ss");
    //console.log(timezone)
    return utcCutoff.clone().tz(timezone);
  };

  const renderTodayOpenHours = () => {
    if (place === undefined || place === null) return "";
    const today = convertToLocalTimeZone(
      moment.utc().format("HH:mm:ss"),
      place.timezone,
    );
    const yesterday = convertToLocalTimeZone(
      moment.utc().format("HH:mm:ss"),
      place.timezone,
    ).subtract(1, "days");
    //console.log(yesterday);

    const now = convertToLocalTimeZone(
      moment.utc().format("HH:mm:ss"),
      place.timezone,
    );

    const yesterday_close_hour = convertToLocalTimeZone(
      place.openHours.find((d) => d.day === yesterday.format("dddd"))?.close,
      place.timezone,
    );

    const yesterday_open_hour = convertToLocalTimeZone(
      place.openHours.find((d) => d.day === yesterday.format("dddd"))?.open,
      place.timezone,
    );

    if (yesterday_open_hour.date() > now.date())
      yesterday_open_hour.subtract(2, "days");
    else yesterday_open_hour.subtract(1, "days");

    if (
      ((yesterday_close_hour.hour() < yesterday_open_hour.hour() ||
        (yesterday_close_hour.hour() === yesterday_open_hour.hour() &&
          yesterday_close_hour.minute() <= yesterday_open_hour.minute())) &&
        yesterday_close_hour.date() - yesterday_open_hour.date() === 2) ||
      yesterday_close_hour.hour() > yesterday_open_hour.hour()
    )
      yesterday_close_hour.subtract(1, "days");
    //console.log(yesterday_close_hour.date())

    if (
      now.isBefore(yesterday_close_hour) &&
      yesterday_open_hour.hour() >= yesterday_close_hour.hour()
    ) {
      const openHour = convertToLocalTimeZone(
        place.openHours.find((d) => d.day === yesterday.format("dddd"))?.open,
        place.timezone,
      ).subtract(1, "days");

      return (
        openHour.format("HH:mm") + " - " + yesterday_close_hour.format("HH:mm")
      );
    } else {
      let closeHour = convertToLocalTimeZone(
        place.openHours.find((d) => d.day === today.format("dddd"))?.close,
        place.timezone,
      );

      let openHour = convertToLocalTimeZone(
        place.openHours.find((d) => d.day === today.format("dddd"))?.open,
        place.timezone,
      );
      if (openHour.format("dddd") !== today.format("dddd"))
        openHour.subtract(1, "days");

      if (
        (closeHour.hour() < openHour.hour() ||
          (closeHour.hour() === openHour.hour() &&
            closeHour.minute() <= openHour.minute())) &&
        closeHour.date() === openHour.date()
      ) {
        closeHour.add(1, "days");
      }

      return openHour.format("HH:mm") + " - " + closeHour.format("HH:mm");
    }
  };

  return (
    <div className="place-page-container">
      <header className="place-page-header">
        {place !== null && (
          <img
            src={place.placeLogo !== null ? place.placeLogo : LOGO2}
            alt=""
            className="place-page-image"
          />
        )}
        <div className="place-page-image-shadow"></div>
      </header>
      <div className="place-page-details-container">
        <h1 className="place-page-title">{place !== null && t(place.title)}</h1>
        {(isAvailable || isBusy) && (
          <div className="place-page-status-toggle-row">
            <span className="place-page-status-label">
              {t("Restaurant mode")}
            </span>
            <button
              type="button"
              className={`place-page-status-toggle ${isBusy ? "busy" : "available"}`}
              onClick={toggleBusyMode}
              disabled={statusUpdating}
            >
              {statusUpdating ? "…" : isBusy ? t("Busy") : t("Available")}
            </button>
            <span className="place-page-status-hint">
              {isBusy
                ? t("Customers cannot place new orders")
                : t("Accepting new orders")}
            </span>
          </div>
        )}
        <div className="place-page-details-row">
          <div
            className="place-page-details-image-wrapper"
            style={{
              borderTopLeftRadius: i18n.language === "en" ? 0 : 15,
              borderBottomLeftRadius: i18n.language === "en" ? 0 : 15,
              borderTopRightRadius: i18n.language === "en" ? 15 : 0,
              borderBottomRightRadius: i18n.language === "en" ? 15 : 0,
            }}
          >
            <img src={Clock} alt="" className="place-page-details-image" />
          </div>
          {place !== null ? (
            // <p className='place-page-details-text'>
            //   {convertToLocalTimeZone(
            //     place.openTime.from_hour,
            //     place.timezone
            //   ).format('HH:mm') +
            //     '  -  ' +
            //     convertToLocalTimeZone(
            //       place.openTime.to_hour,
            //       place.timezone
            //     ).format('HH:mm')}
            // </p>
            <p className="place-page-details-text">{renderTodayOpenHours()}</p>
          ) : (
            <p className="place-page-details-text">{t("close")}</p>
          )}
          <p
            style={{
              right: i18n.language === "en" ? 0 : undefined,
              left: i18n.language === "en" ? undefined : 0,
              background: Colors.PURPLE,
              color: Colors.GREEN,
              width: "50px",
            }}
            className="place-choose-button"
            onClick={(e) => {
              setOpenHoursPopUpIsOpen(true);
            }}
          >
            {t("Edit")}
          </p>
        </div>
        <div className="place-page-details-row">
          <div
            className="place-page-details-image-wrapper"
            style={{
              borderTopLeftRadius: i18n.language === "en" ? 0 : 15,
              borderBottomLeftRadius: i18n.language === "en" ? 0 : 15,
              borderTopRightRadius: i18n.language === "en" ? 15 : 0,
              borderBottomRightRadius: i18n.language === "en" ? 15 : 0,
            }}
          >
            <LocationOnIcon
              className="place-page-details-image"
              style={{ color: Colors.LIGHTERPURPLE }}
            />
          </div>
          <p className="place-page-details-text">
            {place !== null && t(place.location)}
          </p>
        </div>
      </div>
      <div className="place-page-categories-container">
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
          }}
        >
          <p
            style={{
              right: i18n.language === "en" ? 0 : undefined,
              left: i18n.language === "en" ? undefined : 0,
              background: isItems ? Colors.GREEN : Colors.PURPLE,
              color: isItems ? Colors.PURPLE : Colors.GREEN,
            }}
            className="place-choose-button"
            onClick={(e) => {
              setIsItems(true);
            }}
          >
            {t("Items")}
          </p>
          <p
            style={{
              right: i18n.language === "en" ? 0 : undefined,
              left: i18n.language === "en" ? undefined : 0,
              background: !isItems ? Colors.GREEN : Colors.PURPLE,
              color: !isItems ? Colors.PURPLE : Colors.GREEN,
            }}
            className="place-choose-button"
            onClick={(e) => {
              setIsItems(false);
            }}
          >
            {t("SubItems")}
          </p>
        </div>
        {place !== null &&
          isItems &&
          place.categories?.map((category) => (
            <PlaceCategory key={uuidv4()} category={category} />
          ))}
        {place !== null &&
          !isItems &&
          place.categories.map((category) =>
            category.items.map((item) => (
              <PlaceCategory key={uuidv4()} category={item} />
            )),
          )}
      </div>
      <AppPopup
        open={openHoursPopUpIsOpen}
        setOpen={setOpenHoursPopUpIsOpen}
        title={t("Open hours")}
        xButton
        children={
          place !== null && (
            <div className="place-open-hours">
              {initializeOpenHours().map((o, index) => (
                <PlaceOpenHoursRow
                  key={uuidv4()}
                  openHour={o}
                  convertToLocalTimeZone={convertToLocalTimeZone}
                  timezone={place.timezone}
                />
              ))}
            </div>
          )
        }
        height={"90vh"}
      />
    </div>
  );
};

export default observer(Place);
